"use client"

import React, { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import "./splash-screen.css"
import { useModel } from "../../context/ModelContext";
import { loadAndStoreModel, checkModelExists, cancelModelDownload, checkFundusModelExists, loadAndStoreFundusModel, cancelFundusModelDownload, checkEyesModelExists, loadAndStoreEyesModel, cancelEyesModelDownload, checkTrainFeaturesMeanExists, loadAndStoreTrainFeaturesMean, checkTrainFeaturesCovinvExists, loadAndStoreTrainFeaturesCovinv, cleanupOldModelFiles, syncCachedModelsWithServerVersion, persistModelBundleVersionAfterSuccess, invalidateModelDownloadApiCache } from "../../services/modelService";
import * as Sentry from '@sentry/react';
import { getConnectionStrength, isNetworkRelatedError } from "../../utils/connectionStrength";
import * as serviceWorkerRegistration from "../../serviceWorkerRegistration";
import { isMobileBrowser } from "../../utils/deviceDetection";
import { warmOpenCvScriptCache } from "../../utils/opencvOfflineCache";

const SplashScreen = ({ userData }) => {
  const navigate = useNavigate();
  const { loading, error, isModelReady, loadModel, setModelSession, loadFundusModel, isFundusLoading, isFundusReady, loadEyesModel, isEyesLoading, isEyesReady } = useModel();
  // Mobile (iOS Safari, Android Chrome, etc.): skip loading WASM InferenceSessions at splash.
  // The WASM heap can only grow; three sessions pinned at once overflows phone RAM quickly.
  // imageUpload.js loads each session on-demand (one at a time) instead.
  const skipOnnxWarmAtSplash = isMobileBrowser();
  const [loadingText, setLoadingText] = useState("Loading...");
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const downloadStepRef = useRef(0); // 0=DRDME, 1=Fundus, 2=Eyes, 3=mean, 4=covinv (5 steps = 20% each)
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [weakConnectionWarning, setWeakConnectionWarning] = useState(null);
  const [weakConnectionDismissed, setWeakConnectionDismissed] = useState(false);
  const modelLoadAttempted = useRef(false);
  const navigationAttempted = useRef(false);
  const weakConnectionDismissedRef = useRef(false);




  // One progress bar across all 5 steps (20% each). Step progress 0-100 → overall 0-100.
  const setOverallProgress = (stepIndex, stepPercent) => {
    const overall = Math.min(100, Math.round(stepIndex * 20 + (stepPercent * 20) / 100));
    setDownloadProgress(overall);
  };

  // Fixed download order (same on first load and retry):
  // 1. DRDME → 2. Fundus → 3. Eyes → 4. train_features_mean → 5. train_features_covinv
  // For each: if model fully in storage → skip (load from storage). If missing or partial → download (resume from % if partial).
  const handleRetry = async () => {
    cancelModelDownload();
    cancelFundusModelDownload();
    cancelEyesModelDownload();
    invalidateModelDownloadApiCache();
    setIsRetrying(true);
    setShowRetryButton(false);
    setLoadingText("Retrying model download...");
    downloadStepRef.current = 0;
    modelLoadAttempted.current = false;
    navigationAttempted.current = false;

    try {
      const retrySync = await syncCachedModelsWithServerVersion();
      const retryBundleVersion = retrySync.serverVersion;
      // Load DRDME model (regular model)
      const exists = await checkModelExists();
      if (exists) {
        setLoadingText("Preparing models...");
        setShowProgress(false);
        if (!skipOnnxWarmAtSplash) { await loadModel(); }
      } else {
        setLoadingText("Downloading Models");
        setShowProgress(true);
        downloadStepRef.current = 0;
        setOverallProgress(0, 0);
        const modelResult = await loadAndStoreModel(
          (progress) => setOverallProgress(0, progress),
          (resumePercent) => {
            setLoadingText("Downloading Models");
            setOverallProgress(0, resumePercent);
          }
        );
        if (modelResult === null) {
          return;
        }
        setLoadingText("Preparing models...");
        setShowProgress(false);
        if (!skipOnnxWarmAtSplash) { await loadModel(); }
      }

      // Load fundus model
      const fundusExists = await checkFundusModelExists();
      if (fundusExists) {
        setLoadingText("Preparing models...");
        if (!skipOnnxWarmAtSplash) { await loadFundusModel(); }
      } else {
        setLoadingText("Downloading Models");
        setShowProgress(true);
        downloadStepRef.current = 1;
        setOverallProgress(1, 0);
        const fundusResult = await loadAndStoreFundusModel(
          (progress) => setOverallProgress(1, progress),
          (resumePercent) => {
            setLoadingText("Downloading Models");
            setOverallProgress(1, resumePercent);
          }
        );
        if (fundusResult === null) {
          return;
        }
        setLoadingText("Preparing models...");
        setShowProgress(false);
        if (!skipOnnxWarmAtSplash) { await loadFundusModel(); }
      }

      // Load eyes model
      const eyesExists = await checkEyesModelExists();
      if (eyesExists) {
        setLoadingText("Preparing models...");
        if (!skipOnnxWarmAtSplash) { await loadEyesModel(); }
      } else {
        setLoadingText("Downloading Models");
        setShowProgress(true);
        downloadStepRef.current = 2;
        setOverallProgress(2, 0);
        const eyesResult = await loadAndStoreEyesModel(
          (progress) => setOverallProgress(2, progress),
          (resumePercent) => {
            setLoadingText("Downloading Models");
            setOverallProgress(2, resumePercent);
          }
        );
        if (eyesResult === null) {
          return;
        }
        setLoadingText("Preparing models...");
        setShowProgress(false);
        if (!skipOnnxWarmAtSplash) { await loadEyesModel(); }
      }

      // Download train_features_mean binary file
      const meanExists = await checkTrainFeaturesMeanExists();
      if (!meanExists) {
        setLoadingText("Downloading Models");
        setShowProgress(true);
        downloadStepRef.current = 3;
        setOverallProgress(3, 0);
        const meanResult = await loadAndStoreTrainFeaturesMean((progress) => {
          setOverallProgress(3, progress);
        });
        if (meanResult === null) {
          return;
        }
      }

      // Download train_features_covinv binary file
      const covinvExists = await checkTrainFeaturesCovinvExists();
      if (!covinvExists) {
        setLoadingText("Downloading Models");
        setShowProgress(true);
        downloadStepRef.current = 4;
        setOverallProgress(4, 0);
        const covinvResult = await loadAndStoreTrainFeaturesCovinv((progress) => {
          setOverallProgress(4, progress);
        });
        if (covinvResult === null) {
          return;
        }
      }

      // Verify all required models before showing success
      const drdmeOk = await checkModelExists();
      const fundusOk = await checkFundusModelExists();
      const eyesOk = await checkEyesModelExists();
      if (!drdmeOk || !fundusOk || !eyesOk) {
        throw new Error(`Models incomplete after load. DRDME:${drdmeOk} Fundus:${fundusOk} Eyes:${eyesOk}`);
      }

      await persistModelBundleVersionAfterSuccess(retryBundleVersion);

      setShowProgress(false);
      setLoadingText("All models downloaded and saved!");
      setShowSuccessPopup(true);
      
      const optometristRole = localStorage.getItem("optometrist_role");
      
      if (!navigationAttempted.current) {
        navigationAttempted.current = true;
        setTimeout(() => {
          setShowSuccessPopup(false);
          if (optometristRole === "Ophthalmologist") {
            navigate("/patientrecord");
          } else {
            navigate("/dashboard");
          }
        }, 2000); // Show popup for 2 seconds
      }
    } catch (err) {
      setLoadingText(
        isNetworkRelatedError(err)
          ? "Model download failed. This often happens with a slow or unstable internet connection. Please check your connection and try again."
          : "Model download failed. Please try again."
      );
      setShowRetryButton(true);

      // Capture model loading errors in Sentry
      Sentry.captureException(err, {
        tags: {
          errorType: 'model_loading_error',
          errorStage: 'splash_screen_retry',
        },
        extra: {
          errorMessage: err.message,
          errorName: err.name,
        },
      });
    } finally {
      setIsRetrying(false);
    }
  };

// Model loading and role-based navigation
useEffect(() => {
  const token = localStorage.getItem("optha-token");
  const optometristRole = localStorage.getItem("optometrist_role");

  if (!token) {
    navigate("/");
    return;
  }

  const initializeAndNavigate = async () => {
    if (modelLoadAttempted.current) return;
    modelLoadAttempted.current = true;
    // Module-level cache survives logout→login in the same tab; always refetch metadata on splash so model_version is checked every login
    invalidateModelDownloadApiCache();

    try {
      // If a new service worker is still waiting, finish activation here — do not tell users to use the
      // dashboard green bar (they may already have tapped Update App, been logged out, and landed on splash).
      setLoadingText("Checking for updates...");
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        try {
          const reg = await navigator.serviceWorker.getRegistration();
          if (reg?.waiting) {
            setLoadingText("Finishing update — this page will reload in a moment…");
            // applyUpdate() guards against double-calls with __SW_USER_REQUESTED_RELOAD,
            // so calling it here is safe whether or not the dashboard already called it.
            serviceWorkerRegistration.applyUpdate();
            return; // do not start model loading; the reload will restart the page
          }
        } catch (_) {
          /* proceed if registration check fails */
        }
      }

      // Clean up old model files FIRST to free up storage space
      // This runs only ONCE per user (uses localStorage flag) and runs silently
      try {
        await cleanupOldModelFiles();
      } catch {
        /* non-fatal */
      }

      let bundleServerModelVersion = null;
      try {
        const syncResult = await syncCachedModelsWithServerVersion();
        bundleServerModelVersion = syncResult.serverVersion;
      } catch {
        /* non-fatal */
      }

      // No version update - proceed with model download (fixed order: 1.DRDME 2.Fundus 3.Eyes 4.mean 5.covinv; skip complete, resume partial)
      void warmOpenCvScriptCache();
      setLoadingText("Downloading Models");

      // Check connection strength and show non-blocking warning if weak
      const { weak, message } = getConnectionStrength();
      if (weak && message && !weakConnectionDismissed) {
        setWeakConnectionWarning(message);
      }

      // Load regular model
      const exists = await checkModelExists();

      if (exists) {
        setLoadingText("Preparing models...");
        setShowProgress(false);
        if (!skipOnnxWarmAtSplash) { await loadModel(); }
      } else {
        setLoadingText("Downloading Models");
        setShowProgress(true);
        downloadStepRef.current = 0;
        setOverallProgress(0, 0);

        await loadAndStoreModel(
          (progress) => setOverallProgress(0, progress),
          (resumePercent) => {
            setLoadingText("Downloading Models");
            setOverallProgress(0, resumePercent);
          }
        );
        
        // Verify model was downloaded successfully
        const modelExists = await checkModelExists();
        if (!modelExists) {
          throw new Error('Model download completed but model not found in storage');
        }
        
        setLoadingText("Preparing models...");
        setShowProgress(false);
        if (!skipOnnxWarmAtSplash) { await loadModel(); }
      }

      // Load fundus detector model
      setLoadingText("Downloading Models");
      
      const fundusExists = await checkFundusModelExists();
      
      if (fundusExists) {
        setLoadingText("Preparing models...");
        if (!skipOnnxWarmAtSplash) { await loadFundusModel(); }
      } else {
        setLoadingText("Downloading Models");
        setShowProgress(true);
        downloadStepRef.current = 1;
        setOverallProgress(1, 0);
        
        await loadAndStoreFundusModel(
          (progress) => setOverallProgress(1, progress),
          (resumePercent) => {
            setLoadingText("Downloading Models");
            setOverallProgress(1, resumePercent);
          }
        );
        
        const fundusModelExists = await checkFundusModelExists();
        if (!fundusModelExists) {
          throw new Error('Fundus model download completed but model not found in storage. Please retry.');
        }
        setLoadingText("Preparing models...");
        setShowProgress(false);
        if (!skipOnnxWarmAtSplash) { await loadFundusModel(); }
      }

      // Load eyes detector model
      setLoadingText("Downloading Models");
      
      const eyesExists = await checkEyesModelExists();
      
      if (eyesExists) {
        setLoadingText("Preparing models...");
        if (!skipOnnxWarmAtSplash) { await loadEyesModel(); }
      } else {
        setLoadingText("Downloading Models");
        setShowProgress(true);
        downloadStepRef.current = 2;
        setOverallProgress(2, 0);
        
        await loadAndStoreEyesModel(
          (progress) => setOverallProgress(2, progress),
          (resumePercent) => {
            setLoadingText("Downloading Models");
            setOverallProgress(2, resumePercent);
          }
        );
        
        const eyesModelExists = await checkEyesModelExists();
        if (!eyesModelExists) {
          throw new Error('Eyes model download completed but model not found in storage. Please retry.');
        }
        setLoadingText("Preparing models...");
        setShowProgress(false);
        if (!skipOnnxWarmAtSplash) { await loadEyesModel(); }
      }

      // Download train_features_mean binary file
      setLoadingText("Downloading Models");
      
      const meanExists = await checkTrainFeaturesMeanExists();
      
      if (!meanExists) {
        setLoadingText("Downloading Models");
        setShowProgress(true);
        downloadStepRef.current = 3;
        setOverallProgress(3, 0);
        
        await loadAndStoreTrainFeaturesMean((progress) => {
          setOverallProgress(3, progress);
        });
      }

      // Download train_features_covinv binary file
      setLoadingText("Downloading Models");
      
      const covinvExists = await checkTrainFeaturesCovinvExists();
      
      if (!covinvExists) {
        setLoadingText("Downloading Models");
        setShowProgress(true);
        downloadStepRef.current = 4;
        setOverallProgress(4, 0);
        
        await loadAndStoreTrainFeaturesCovinv((progress) => {
          setOverallProgress(4, progress);
        });
      }

      // Final check: do not show success unless all required models are in storage
      const drdmeOk = await checkModelExists();
      const fundusOk = await checkFundusModelExists();
      const eyesOk = await checkEyesModelExists();
      if (!drdmeOk || !fundusOk || !eyesOk) {
        throw new Error(`Models incomplete. DRDME:${drdmeOk} Fundus:${fundusOk} Eyes:${eyesOk}. Please retry.`);
      }

      await persistModelBundleVersionAfterSuccess(bundleServerModelVersion);

      setShowProgress(false);
      setLoadingText("All models downloaded and saved!");
      setShowSuccessPopup(true);

      if (!navigationAttempted.current) {
        navigationAttempted.current = true;
        setTimeout(() => {
          setShowSuccessPopup(false);
          if (optometristRole === "Ophthalmologist") {
            navigate("/patientrecord");
          } else {
            navigate("/dashboard");
          }
        }, 2000); // Show popup for 2 seconds
      }
    } catch (err) {
      // User clicked Retry → we cancelled this run; don't show error, let retry flow handle it
      if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
        return;
      }
      // Any other error: show retry button
      let errorMessage = "Model loading failed. Please try again.";
      if (err.message?.includes('Storage full') || err.message?.includes('Storage quota')) {
        errorMessage = err.message || "Storage is full. Please sync your data or clear browser storage and retry.";
      } else if (err.message?.includes('ONNX Runtime not loaded')) {
        errorMessage = "AI engine not ready. Please refresh the page.";
      } else if (err.message?.includes('Failed to get model data')) {
        errorMessage = "Model data corrupted. Please retry download.";
      } else if (err.message?.includes('not found in storage')) {
        errorMessage = "Model download incomplete. Please retry.";
      } else if (err.message?.includes('memory') || err.message?.includes('quota')) {
        errorMessage = "Not enough storage space. Please free up space and retry.";
      } else if (isNetworkRelatedError(err)) {
        errorMessage = "Model download failed. This often happens with a slow or unstable internet connection. Please check your connection and try again.";
      }
      
      setLoadingText(errorMessage);
      setShowRetryButton(true);
      setShowProgress(false);
      
      Sentry.captureException(err, {
        tags: {
          errorType: 'model_loading_error',
          errorStage: 'splash_screen',
        },
        extra: {
          errorMessage: err.message,
          errorName: err.name,
          stack: err.stack,
        },
      });
    }
  };

  initializeAndNavigate();
}, [loadModel, navigate]);

// Listen for online/offline events
useEffect(() => {
  let offlineCheckTimeout = null;

  const handleOnline = () => {
    if (showRetryButton) {
      setLoadingText("Connection restored. You can now retry the download.");
    } else if (isRetrying) {
      setLoadingText("Connection restored. Continuing download...");
    }
  };

  const handleOffline = () => {
    // Don't immediately show retry button - Safari iOS sometimes incorrectly triggers offline event
    // Wait a bit to see if connection is actually lost or just slow
    if (offlineCheckTimeout) {
      clearTimeout(offlineCheckTimeout);
    }

    offlineCheckTimeout = setTimeout(() => {
      // Double-check if still offline after delay
      if (!navigator.onLine) {
        if (showRetryButton) {
          setLoadingText("No internet connection. Please check your network and try again.");
        } else if (isRetrying) {
          setLoadingText("Connection lost. Please check your network and retry.");
          setShowRetryButton(true);
        } else if (showProgress) {
          // Only show retry if download was actually in progress
          setLoadingText("Connection lost during download. Please check your network and retry.");
          setShowRetryButton(true);
        }
      }
    }, 3000); // Wait 3 seconds before showing retry (Safari iOS false positive protection)
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    if (offlineCheckTimeout) {
      clearTimeout(offlineCheckTimeout);
    }
  };
}, [showRetryButton, isRetrying, showProgress]);

// Keep ref in sync so connection-change handler sees latest dismiss state
  useEffect(() => {
    weakConnectionDismissedRef.current = weakConnectionDismissed;
  }, [weakConnectionDismissed]);

  // Re-check connection when it changes (e.g. user switches from Slow 3G to No throttling) – hide banner when no longer weak
  useEffect(() => {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn || typeof conn.addEventListener !== 'function') return;

    const handleChange = () => {
      const { weak, message } = getConnectionStrength();
      if (!weak) {
        setWeakConnectionWarning(null);
      } else if (!weakConnectionDismissedRef.current && message) {
        setWeakConnectionWarning(message);
      }
    };

    conn.addEventListener('change', handleChange);
    return () => conn.removeEventListener('change', handleChange);
  }, []);

// Cleanup on component unmount
useEffect(() => {
  return () => {
    // Cancel any ongoing model download when component unmounts
    cancelModelDownload();
  };
}, []);


  return (
    <div className="splash-container">
      <div className="splash-content">
        {/* Logo and Title Section */}
        <div className="logo-title-container">
          <img
            className='dia-img'
            src="/assests/images/MadhuNetrAI Logo.png"
            alt='Madhu Netra'
          />
        </div>

        {/* Subtitle */}
        <h2 className="subtitle">
          <span className="subtitle-first">AI Powered Diabetic</span>
          <br />
          <span className="subtitle-second">Retinopathy Screening Tool</span>
        </h2>

        {/* Weak connection warning (non-blocking) */}
        {weakConnectionWarning && !weakConnectionDismissed && (
          <div className="weak-connection-banner">
            <span className="weak-connection-text">⚠️ {weakConnectionWarning}</span>
            <button
              type="button"
              className="weak-connection-dismiss"
              onClick={() => {
                setWeakConnectionDismissed(true);
                setWeakConnectionWarning(null);
              }}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        <div className="loading-status">
          {loadingText}
        </div>

        {/* Progress Bar */}
        {showProgress && !showRetryButton && (
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${downloadProgress}%` }}
              ></div>
            </div>
            <div className="progress-text">
              {downloadProgress}%
            </div>
          </div>
        )}

        {/* Retry Button */}
        {showRetryButton && (
          <div className="retry-container">
            <button 
              className="retry-button" 
              onClick={handleRetry}
              disabled={isRetrying}
            >
              {isRetrying ? "Retrying..." : "Retry Download"}
            </button>
          </div>
        )}

        {/* Success Popup */}
        {showSuccessPopup && (
          <div className="success-popup">
            <div className="success-content">
              <div className="success-icon">✅</div>
              <div className="success-title">Model Ready!</div>
              <div className="success-message">
                AI model has been successfully downloaded and loaded. Redirecting to dashboard...
              </div>
            </div>
          </div>
        )}

        <div className="spacer"></div>
        {/* Footer Logos */}
        <div className="footer-logos">
          <img className="footer-logo" src="/assests/images/New-Logos.png" alt="Indian Government" />
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;

